import React, { FC } from 'react'
import OdinLogo from '@assets/icons/odin.webp'
import WaterNeuronLogo from '@plug-core/assets/icons/waterneuron.png'
import OneSecLogo from '@plug-core/assets/icons/onesec.png'
import { ROUTES } from '@utils/routes'
import {
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  useTheme,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Chain } from '@plug-core/interfaces/common'
import { ODINBTC_ID } from '@plug-core/constants/tokenIds'
import { IoCloseCircle } from 'react-icons/io5'
import { usePromo } from '@plug-core/state'
import Carousel from '@components/Carousel'
import useWindowDimensions from '@hooks/useWindowDimensions'
import { BounceContainer } from '@components/BounceContainer/BounceContainer'

interface PromoItem {
  id: string
  title: string
  description: string
  image: any
  route: string
  navState?: any
}

const PROMO_FEATURES: PromoItem[] = [
  {
    id: '1',
    title: 'Stake ICP with 14% APY',
    description: 'Try Waterneuron DAO liquid staking',
    image: WaterNeuronLogo,
    route: ROUTES.STAKE,
  },
  {
    id: '2',
    title: 'Odin swaps are here!',
    description: 'Trade 200+ Odin runes in a few clicks',
    image: OdinLogo,
    route: ROUTES.SWAP,
    navState: {
      chain: Chain.ODIN,
      fromTokenId: ODINBTC_ID,
    },
  },
  {
    id: '3',
    title: 'Bridge with OneSec',
    description: 'Bridge USDC from ETH to ICP and back!',
    image: OneSecLogo,
    route: ROUTES.BRIDGE,
  },
]

interface PromoItemProps {
  item: PromoItem
  onDismiss: (id: string) => void
}

const PromoItem: FC<PromoItemProps> = ({ item, onDismiss }) => {
  const navigate = useNavigate()

  const { palette } = useTheme()
  const { width } = useWindowDimensions()

  return (
    <BounceContainer>
      <Stack width={width - 30} sx={{ paddingLeft: '15px', paddingRight: '15px' }}>
        <ListItem
          disablePadding
          sx={{
            bgcolor: palette.background.paper,
            paddingRight: 2.5,
            paddingLeft: 2.25,
            paddingY: 0.75,
            marginY: 0.6,
            borderRadius: 1.5,
            cursor: 'pointer',
            ':hover': {
              bgcolor: '#23242b',
            },
            height: 90,
          }}
          onClick={() => navigate(item.route, { state: item.navState })}
        >
          <ListItemAvatar>
            <Avatar src={item.image} />
          </ListItemAvatar>
          <ListItemText
            primary={item.title}
            primaryTypographyProps={{
              sx: {
                display: '-webkit-box',
                WebkitLineClamp: 1,
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
              },
            }}
            secondaryTypographyProps={{
              sx: {
                mr: 2,
              },
            }}
            secondary={item.description}
          />
          <ListItemSecondaryAction>
            <Box mt={-4.2}>
              <IconButton
                size="small"
                onClick={(e) => {
                  onDismiss(item.id)
                }}
                sx={{
                  color: 'text.secondary',
                  bgcolor: 'background.paper',
                }}
              >
                <IoCloseCircle fontSize="medium" />
              </IconButton>
            </Box>
          </ListItemSecondaryAction>
        </ListItem>
      </Stack>
    </BounceContainer>
  )
}

const Promo = () => {
  const { closePromo, closedPromos } = usePromo()
  const { width } = useWindowDimensions()

  const availablePromos = PROMO_FEATURES.filter((p) => !closedPromos.includes(p.id))

  if (!availablePromos.length) return

  return (
    <Box>
      <List>
        <Carousel itemSize={width} spacingPx={20} hideControls showPager>
          {availablePromos.map((promo) => (
            <PromoItem key={promo.id} item={promo} onDismiss={closePromo} />
          ))}
        </Carousel>
      </List>
    </Box>
  )
}

export default Promo
